import { makeAutoObservable } from "mobx";
import { AttributeShelf, FetchShelf, LoaderShelf } from "../../../shelves";
import { onlyNumber } from "../../../utils/formatters";
import { api } from "../../../services";
import type { apiTypes } from "../../../services";
import type { AuthenticationStore, FintechRegisterStore } from "../../../stores";
import type { AxiosError } from "axios";
import { strings } from "../../../localization";

export class Store {
	public loader = new LoaderShelf();
	public postCustomersPersonalPostAddressFetch: FetchShelf<apiTypes.PersonalPostAddressInfo>;
	public patchCustomersPersonalPostAddressFetch: FetchShelf<apiTypes.PersonalPostAddressInfo>;
	public getAddressByPostalCode: FetchShelf<apiTypes.SimpleAddressResponse>;
	public hasPostalCodeError = new AttributeShelf(false);
	public addressFieldError = new AttributeShelf(false);
	public addressFieldErrorMessage = new AttributeShelf("");

	constructor(public authentication: AuthenticationStore, public fintechRegisterStore: FintechRegisterStore) {
		const authToken = authentication.getToken() || "";

		this.postCustomersPersonalPostAddressFetch = new FetchShelf(async () =>
			api.postCustomersPersonalPostalAddress(this.addressFieldsData, authToken),
		);
		this.patchCustomersPersonalPostAddressFetch = new FetchShelf(async () =>
			api.patchCustomersPersonalPostalAddress(
				fintechRegisterStore.postalCodeAddressId.value,
				this.addressFieldsData,
				authToken,
			),
		);
		this.getAddressByPostalCode = new FetchShelf(
			async () => {
				this.hasPostalCodeError.set(false);
				return await api.getAddressByPostalCode(
					onlyNumber(this.fintechRegisterStore.postalCode.field.value),
					authToken,
				);
			},
			this.setAddressData,
			(error: AxiosError) => {
				if (error.response?.status === 404) {
					this.hasPostalCodeError.set(true);
				}
			},
		);

		makeAutoObservable(this);
	}

	public get addressFieldsData(): apiTypes.PersonalPostAddressInfo {
		return {
			isMain: true,
			address: this.trimString(this.fintechRegisterStore.address.field.value),
			additionalInfo: this.trimString(this.fintechRegisterStore.additionalInfo.field.value),
			townName: this.trimString(this.fintechRegisterStore.townName.field.value),
			districtName: this.trimString(this.fintechRegisterStore.districtName.field.value),
			countrySubDivision: this.trimString(this.fintechRegisterStore.countrySubDivision.field.value),
			number: this.fintechRegisterStore.number.field.value,
			postalCode: onlyNumber(this.fintechRegisterStore.postalCode.field.value),
			country: "Brasil",
		};
	}

	public get isSubmitButtonDisabled(): boolean {
		return (
			this.fintechRegisterStore.address.field.value === "" ||
			this.fintechRegisterStore.districtName.field.value === "" ||
			this.fintechRegisterStore.number.field.value === "" ||
			this.fintechRegisterStore.postalCode.field.value.length < 9 ||
			this.fintechRegisterStore.townName.field.value === "" ||
			this.fintechRegisterStore.countrySubDivision.field.value === "" ||
			this.hasPostalCodeError.value ||
			this.addressFieldError.value ||
			this.isLoading
		);
	}

	public get isLoading(): boolean {
		return (
			this.loader.isLoading ||
			this.postCustomersPersonalPostAddressFetch.loader.isLoading ||
			this.patchCustomersPersonalPostAddressFetch.loader.isLoading
		);
	}

	public setAddressData = (data: apiTypes.SimpleAddressResponse): void => {
		this.fintechRegisterStore.address.field.set(data.address);
		this.fintechRegisterStore.districtName.field.set(data.districtName);
		this.fintechRegisterStore.townName.field.set(data.townName);
		this.fintechRegisterStore.countrySubDivision.field.set(data.countrySubDivision);
	};

	// Método para sanitizar o endereço (remover caracteres especiais)
	public sanitizeAddress = (): void => {
		const currentValue = this.fintechRegisterStore.address.field.value;

		// Remover pontos e outros caracteres especiais, mantendo apenas:
		// - Letras (incluindo acentuadas)
		// - Números
		const sanitizedValue = currentValue.replace(/[^\w\sáàâãéèêíïóôõöúçñÁÀÂÃÉÈÊÍÏÓÔÕÖÚÇÑ]/g, "");

		// Atualizar o valor apenas se houve mudança
		if (sanitizedValue !== currentValue) {
			this.fintechRegisterStore.address.field.set(sanitizedValue);
		}
	};

	// Método para validar o endereço antes de enviar
	public validateAddress = (): boolean => {
		const addressValue = this.fintechRegisterStore.address.field.value;

		// Verificar se ainda há caracteres especiais após a sanitização
		const hasSpecialChars = /[^\w\sáàâãéèêíïóôõöúçñÁÀÂÃÉÈÊÍÏÓÔÕÖÚÇÑ]/g.test(addressValue);

		if (hasSpecialChars) {
			this.addressFieldError.set(true);
			this.addressFieldErrorMessage.set(strings.components.addressInputGroup.addressSpecialCharsValidationError);
			return false;
		}

		// Limpar erro se não houver mais caracteres especiais
		this.addressFieldError.set(false);
		return true;
	};

	public fetchData = async (onSuccess: VoidFunction): Promise<void> => {
		if (this.loader.isLoading) {
			return;
		}

		this.loader.start();
		try {
			if (this.fintechRegisterStore.postalCodeAddressId.value) {
				await api.patchCustomersPersonalPostalAddress(
					this.fintechRegisterStore.postalCodeAddressId.value,
					this.addressFieldsData,
					this.authentication.getToken() || "",
				);
			} else {
				await api.postCustomersPersonalPostalAddress(this.addressFieldsData, this.authentication.getToken() || "");
			}

			onSuccess();
		} catch (e) {
			const error = e as AxiosError<{ errors?: Array<{ field: string; message: string }> }>;

			// Verificar se é o erro específico de caracteres especiais no endereço
			if (
				error.response?.status === 400 &&
				error.response.data.errors?.some(
					(err) =>
						err.field === "address" &&
						(err.message.includes("special characters") || err.message.includes("must not contain")),
				)
			) {
				// Definir mensagem de erro específica
				this.addressFieldError.set(true);
				this.addressFieldErrorMessage.set(strings.components.addressInputGroup.addressSpecialCharsError);
			} else {
				// Para outros erros, apenas logar
				window.console.error(e);
			}
		} finally {
			this.loader.end();
		}
	};

	// função privada para remover espaços em branco de uma string ou retornar uma string vazia
	private trimString = (value: string | undefined): string => value?.trim() || "";
}
